import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, LogOut as LogOutIcon, LogOut, FileDown, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

export default function EmployeeDashboard() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const todayRecord = attendance.find((a) => a.date === today);

  const fetchAll = async () => {
    try {
      const [p, a, pay] = await Promise.all([
        axios.get(`${API}/employees/me`, { withCredentials: true }),
        axios.get(`${API}/attendance/my`, { withCredentials: true }),
        axios.get(`${API}/payroll/my`, { withCredentials: true }),
      ]);
      setProfile(p.data);
      setAttendance(a.data);
      setPayslips(pay.data);
    } catch {
      toast.error(t.enquiry.error);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const punch = async (action) => {
    setLoading(true);
    try {
      await axios.post(`${API}/attendance/${action}`, {}, { withCredentials: true });
      toast.success(action === "check-in" ? t.emp.checkIn : t.emp.checkOut);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="employee-dashboard" className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Aadisarvtra logo" className="h-9 w-9 rounded-lg object-cover" />
            <div className="leading-tight">
              <h1 className="font-heading text-base font-bold">{t.emp.portal}</h1>
              <p className="text-xs text-muted-foreground">
                {t.emp.welcome}, {profile?.name || user?.name}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" data-testid="employee-logout-button" onClick={handleLogout} className="gap-2">
            <LogOut className="h-4 w-4" /> {t.emp.logout}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="today-attendance-card">
          <h2 className="font-heading text-lg font-bold">{t.emp.today} — {today}</h2>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {!todayRecord && (
              <>
                <p data-testid="attendance-status-text" className="text-sm text-muted-foreground">{t.emp.notCheckedIn}</p>
                <Button onClick={() => punch("check-in")} disabled={loading} className="gap-2" data-testid="check-in-button">
                  <LogIn className="h-4 w-4" /> {t.emp.checkIn}
                </Button>
              </>
            )}
            {todayRecord && !todayRecord.check_out && (
              <>
                <p data-testid="attendance-status-text" className="text-sm text-muted-foreground">
                  {t.emp.checkedInAt}: <strong className="text-foreground">{fmtTime(todayRecord.check_in)}</strong>
                </p>
                <Button onClick={() => punch("check-out")} disabled={loading} className="gap-2" data-testid="check-out-button">
                  <LogOutIcon className="h-4 w-4" /> {t.emp.checkOut}
                </Button>
              </>
            )}
            {todayRecord?.check_out && (
              <p data-testid="attendance-status-text" className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                {t.emp.done} ({t.emp.checkedInAt}: {fmtTime(todayRecord.check_in)}, {t.emp.checkedOutAt}: {fmtTime(todayRecord.check_out)})
              </p>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-lg font-bold">{t.emp.myAttendance}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
            <Table data-testid="my-attendance-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.emp.date}</TableHead>
                  <TableHead>{t.emp.checkIn}</TableHead>
                  <TableHead>{t.emp.checkOut}</TableHead>
                  <TableHead>{t.emp.hours}</TableHead>
                  <TableHead>{t.emp.overtime}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendance.map((a) => (
                  <TableRow key={a.id} data-testid={`my-attendance-row-${a.id}`}>
                    <TableCell>{a.date}</TableCell>
                    <TableCell>{fmtTime(a.check_in)}</TableCell>
                    <TableCell>{fmtTime(a.check_out)}</TableCell>
                    <TableCell>{a.hours}</TableCell>
                    <TableCell>{a.overtime_hours}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {attendance.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground">{t.emp.noRecords}</p>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-lg font-bold">{t.emp.myPayslips}</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
            <Table data-testid="my-payslips-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.emp.month}</TableHead>
                  <TableHead>{t.emp.gross}</TableHead>
                  <TableHead>{t.emp.netPay}</TableHead>
                  <TableHead>{t.emp.status}</TableHead>
                  <TableHead>{t.emp.download}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map((p) => (
                  <TableRow key={p.id} data-testid={`payslip-row-${p.id}`}>
                    <TableCell>{p.month}</TableCell>
                    <TableCell>₹{p.gross}</TableCell>
                    <TableCell className="font-semibold">₹{p.net_pay}</TableCell>
                    <TableCell>{p.status}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        data-testid={`payslip-download-${p.id}`}
                        onClick={() => window.open(`${API}/payroll/${p.id}/payslip`, "_blank")}
                      >
                        <FileDown className="h-3.5 w-3.5" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {payslips.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground">{t.emp.noRecords}</p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
